/** Pantalla 2 (docs/FASE-1.md §6): listado paginado con búsqueda; alta de organización. */
import type { Organization } from "@continnua/contracts";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement } from "react";
import { Link } from "react-router-dom";

import { organizationsApi, type ListOrganizationsParams } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { toDisplayMessage } from "../lib/errors";
import { debounce } from "../lib/query";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todos los estados" },
  { value: "active", label: "Activa" },
  { value: "suspended", label: "Suspendida" },
  { value: "closed", label: "Cerrada" },
];

const STATUS_LABEL: Record<string, string> = { active: "Activa", suspended: "Suspendida", closed: "Cerrada" };

function CreateOrganizationModal({ onClose, onCreated }: { onClose: () => void; onCreated: (org: Organization) => void }): ReactElement {
  const [slug, setSlug] = useState("");
  const [legalName, setLegalName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const org = await organizationsApi.create({
        slug,
        legalName,
        country: "PE",
        timezone: "America/Lima",
        branding: {},
        ...(displayName ? { displayName } : {}),
        ...(taxId ? { taxId } : {}),
      });
      onCreated(org);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Nueva organización" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Slug" hint="Minúsculas, dígitos y guiones. Se usa en URLs, no se puede cambiar luego.">
          {(props) => (
            <input {...props} required pattern="[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?" value={slug} onChange={(e) => setSlug(e.target.value)} />
          )}
        </Field>
        <Field label="Razón social">
          {(props) => <input {...props} required minLength={2} value={legalName} onChange={(e) => setLegalName(e.target.value)} />}
        </Field>
        <Field label="Nombre comercial" hint="Opcional; si se omite, se usa la razón social.">
          {(props) => <input {...props} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}
        </Field>
        <Field label="RUC" hint="Opcional.">
          {(props) => <input {...props} value={taxId} onChange={(e) => setTaxId(e.target.value)} />}
        </Field>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Creando…" : "Crear organización"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function Organizations(): ReactElement {
  const [items, setItems] = useState<Organization[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const requestSeq = useRef(0);

  const load = useCallback(async (params: ListOrganizationsParams, append: boolean) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const page = await organizationsApi.list(params);
      if (seq !== requestSeq.current) return;
      setItems((prev) => (append ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(toDisplayMessage(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  const debouncedLoad = useMemo(() => debounce((params: ListOrganizationsParams) => void load(params, false), 300), [load]);

  useEffect(() => {
    debouncedLoad({ q, ...(status ? { status: status as "active" | "suspended" | "closed" } : {}) });
  }, [q, status, debouncedLoad]);

  const columns: DataTableColumn<Organization>[] = [
    { key: "name", header: "Organización", render: (org) => <Link to={`/organizations/${org.id}`}>{org.displayName}</Link> },
    { key: "slug", header: "Slug", render: (org) => org.slug },
    { key: "country", header: "País", render: (org) => org.country },
    {
      key: "status",
      header: "Estado",
      render: (org) => <Badge tone={toneForStatus(org.status)}>{STATUS_LABEL[org.status] ?? org.status}</Badge>,
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h1>Organizaciones</h1>
        <button type="button" className="button-primary" onClick={() => setShowCreate(true)}>
          Nueva organización
        </button>
      </div>

      <div className="filters-bar">
        <label className="visually-hidden" htmlFor="org-search">
          Buscar organización
        </label>
        <input
          id="org-search"
          type="search"
          placeholder="Buscar por nombre o slug…"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <label className="visually-hidden" htmlFor="org-status-filter">
          Filtrar por estado
        </label>
        <select id="org-status-filter" value={status} onChange={(event) => setStatus(event.target.value)}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}

      <DataTable columns={columns} rows={items} rowKey={(org) => org.id} emptyMessage={loading ? "Cargando…" : "No hay organizaciones."} />

      {nextCursor && (
        <button
          type="button"
          className="button-secondary"
          disabled={loading}
          onClick={() =>
            void load({ q, ...(status ? { status: status as "active" | "suspended" | "closed" } : {}), cursor: nextCursor }, true)
          }
        >
          {loading ? "Cargando…" : "Cargar más"}
        </button>
      )}

      {showCreate && (
        <CreateOrganizationModal
          onClose={() => setShowCreate(false)}
          onCreated={(org) => {
            setShowCreate(false);
            setItems((prev) => [org, ...prev]);
          }}
        />
      )}
    </section>
  );
}
